// AuthCallback — receives Google OAuth redirect from Emergent Auth.
// URL pattern: /auth/callback?trial=1#session_id=xxxxxx
// Reads session_id from the URL fragment, exchanges it for a Panorix JWT,
// stores credentials, then routes the user into the app.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Loader2, AlertCircle } from 'lucide-react';
import { PanorixLogoCompact } from '@/components/PanorixLogo';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchPermissions, setUserData } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');
    const search = new URLSearchParams(location.search);
    const isTrial = search.get('trial') === '1';

    if (!sessionId) {
      setError('Missing Google sign-in token. Please try again.');
      return;
    }

    (async () => {
      try {
        const r = await api.post('/auth/google-callback', {
          session_id: sessionId,
          is_trial: isTrial,
        });
        const data = r.data || {};
        if (data.token || data.accessToken) {
          localStorage.setItem('ciq_token', data.token || data.accessToken);
        }
        if (data.refreshToken) localStorage.setItem('ciq_refresh_token', data.refreshToken);
        if (data.user) localStorage.setItem('ciq_user', JSON.stringify(data.user));
        if (setUserData && data.user) setUserData(data.user);
        window.history.replaceState({}, document.title, '/auth/callback');
        try { await fetchPermissions?.(); } catch {}
        if (data.isTrial) {
          navigate('/project-selector?trial=1', { replace: true });
        } else {
          navigate('/project-selector', { replace: true });
        }
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Sign-in failed.';
        setError(msg);
      }
    })();
  }, [location.search, navigate, fetchPermissions, setUserData]);

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100 flex flex-col items-center justify-center px-6" style={{ fontFamily: '"DM Sans","Inter",sans-serif' }}>
      <PanorixLogoCompact size={140}/>
      <div className="mt-10 max-w-md w-full text-center rounded-xl p-8" style={{ background: 'rgba(11,18,32,0.7)', border: '1px solid rgba(30,78,140,0.4)' }}>
        {!error ? (
          <>
            <Loader2 className="w-7 h-7 animate-spin mx-auto opacity-70 mb-4"/>
            <p className="text-sm text-slate-300">Setting up your trial…</p>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-2">Verifying with Google</p>
          </>
        ) : (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-amber-400 mb-3"/>
            <h1 className="text-lg font-medium mb-1">Sign-in failed</h1>
            <p className="text-sm text-slate-400 mb-6">{error}</p>
            <button onClick={() => navigate('/', { replace: true })}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] bg-[#1E4E8C] hover:bg-[#143A6B] text-white px-5 py-2.5 rounded">
              ← Back to home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
